// Org setup wizard — Owner-only first-run: name, slug, first group (§5.2).

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

type Step = 'name' | 'group' | 'done';

export function OrgSetupPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('name');
  const [orgName, setOrgName] = useState('');
  const [groupName, setGroupName] = useState('');
  const [saving, setSaving] = useState(false);

  if (!user) return null;

  async function handleNameStep(e: FormEvent) {
    e.preventDefault();
    if (!orgName.trim()) return;
    setStep('group');
  }

  async function handleGroupStep(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    // TODO: wire to POST /v1/orgs + POST /v1/orgs/:id/groups
    await new Promise((r) => setTimeout(r, 600));
    setSaving(false);
    setStep('done');
  }

  const steps: Step[] = ['name', 'group', 'done'];
  const stepIndex = steps.indexOf(step);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100dvh',
        padding: 'var(--space-6)',
        background: 'var(--color-bg)',
      }}
    >
      <div style={{ width: '100%', maxWidth: '520px' }}>
        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-8)' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--color-accent)', letterSpacing: '-0.03em' }}>
            Anchor
          </h1>
          <p style={{ color: 'var(--color-text-muted)', marginTop: 'var(--space-1)' }}>
            Set up your organization
          </p>
        </div>

        {/* Step indicator */}
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            marginBottom: 'var(--space-6)',
            justifyContent: 'center',
          }}
        >
          {steps.filter((s) => s !== 'done').map((s, i) => (
            <div
              key={s}
              style={{
                width: 32,
                height: 6,
                borderRadius: 9999,
                background: i <= stepIndex
                  ? 'var(--color-accent)'
                  : 'var(--color-border)',
                transition: 'background var(--transition)',
              }}
              aria-hidden
            />
          ))}
        </div>

        <div className="card">
          {step === 'name' && (
            <>
              <h2 style={{ fontSize: '1.125rem', marginBottom: 'var(--space-5)' }}>
                What's your organization called?
              </h2>
              <form onSubmit={handleNameStep}>
                <div className="form-group" style={{ marginBottom: 'var(--space-5)' }}>
                  <label className="form-label" htmlFor="org-name">Organization name</label>
                  <input
                    id="org-name"
                    className="form-input"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    placeholder="Acme Corp"
                    required
                    autoFocus
                  />
                  <p className="form-hint">This is what members see when they join.</p>
                </div>
                <button type="submit" className="btn btn--primary" disabled={!orgName.trim()}>
                  Continue
                </button>
              </form>
            </>
          )}

          {step === 'group' && (
            <>
              <h2 style={{ fontSize: '1.125rem', marginBottom: 'var(--space-2)' }}>
                Create your first group
              </h2>
              <p style={{ fontSize: '0.9375rem', color: 'var(--color-text-muted)', marginBottom: 'var(--space-5)' }}>
                Groups organize your teams. You can add more later.
              </p>
              <form onSubmit={handleGroupStep}>
                <div className="form-group" style={{ marginBottom: 'var(--space-5)' }}>
                  <label className="form-label" htmlFor="group-name-setup">First group name</label>
                  <input
                    id="group-name-setup"
                    className="form-input"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder="e.g. All Hands, Engineering, Leadership…"
                    autoFocus
                  />
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => setStep('name')}
                  >
                    Back
                  </button>
                  <button type="submit" className="btn btn--primary" disabled={saving || !groupName.trim()}>
                    {saving ? 'Creating…' : 'Create organization'}
                  </button>
                </div>
              </form>
            </>
          )}

          {step === 'done' && (
            <div style={{ textAlign: 'center', padding: 'var(--space-4) 0' }}>
              <p style={{ fontSize: '2rem', marginBottom: 'var(--space-4)' }} aria-hidden>🎉</p>
              <h2 style={{ fontSize: '1.25rem', marginBottom: 'var(--space-3)' }}>
                {orgName} is ready
              </h2>
              <p style={{ fontSize: '0.9375rem', color: 'var(--color-text-muted)', marginBottom: 'var(--space-6)' }}>
                Invite your team, share the join code from Settings, or start with
                the Connection Question bank.
              </p>
              <button
                className="btn btn--primary"
                onClick={() => navigate('/')}
              >
                Go to dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
